import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Label, Input } from "@tellescope/design-system";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Button, Alert, AlertDescription, InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator, LanguageSelector, DobTextInput } from "@tellescope/ui";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import {
  useEnduserBeginLogin,
  useEnduserAuthenticatePassword,
  useEnduserVerifyLoginOtp,
  useEnduserResendLoginOtp,
  useEnduserIsLoading,
  useEnduserIsAuthenticated,
  useEnduserLoginFlowError,
  useEnduserLoginFlowOtpToken,
  useEnduserClearLoginFlow,
  useEnduserStore,
} from "../store/hooks";
import { EngagementEventTypes } from "../tracking/eventTypes";
import { useTrackEngagementEvent } from "../hooks/useTrackEngagementEvent";
import {
  useIsRegistrationDisabled,
  useIsLandingPageDisabled,
  useLoginDescription,
  useLoginInputLabel,
  usePortalSchemaHasAttemptedFetch,
  usePortalSchemaStore,
} from "../../shared/store/portalSchemaStore";
import { useOrganization } from "../../shared/store/organizationStore";
import { LoadingScreen } from "../../shared/components/LoadingScreen";
import type { LoginFlowResult } from "../store/types";

const RESEND_COOLDOWN_SECONDS = 60;

// Derive the current UI step from the login flow result
const getStepFromResult = (result: LoginFlowResult | null) => {
  if (!result) return "email-entry" as const;
  switch (result.result) {
    case "continue-with-password":
      return "password-entry" as const;
    case "sent-email":
    case "sent-sms":
      return "otp-entry" as const;
    case "requires-dob":
      return "dob-entry" as const;
  }
};

export const EnduserLoginPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();

  // Store hooks
  const beginLogin = useEnduserBeginLogin();
  const authenticatePassword = useEnduserAuthenticatePassword();
  const verifyLoginOtp = useEnduserVerifyLoginOtp();
  const resendLoginOtp = useEnduserResendLoginOtp();
  const isLoading = useEnduserIsLoading();
  const isAuthenticated = useEnduserIsAuthenticated();
  const loginFlowError = useEnduserLoginFlowError();
  const loginFlowOtpToken = useEnduserLoginFlowOtpToken();
  const clearLoginFlow = useEnduserClearLoginFlow();
  const isRegistrationDisabled = useIsRegistrationDisabled();
  const isLandingPageDisabled = useIsLandingPageDisabled();
  const loginDescription = useLoginDescription();
  const loginInputLabel = useLoginInputLabel();
  const organization = useOrganization();
  const fetchSchema = usePortalSchemaStore((state) => state.fetchSchema);
  const hasAttemptedSchemaFetch = usePortalSchemaHasAttemptedFetch();
  const { track } = useTrackEngagementEvent();

  // Local state
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loginResult, setLoginResult] = useState<LoginFlowResult | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dobError, setDobError] = useState("");

  // Derive step from loginResult
  const step = getStepFromResult(loginResult);

  // This route can be the first one a user visits (deep link, bookmark, new tab), so the
  // schema may not have been fetched yet by EnduserRouteHandler. Fetch it here too so
  // isRegistrationDisabled/isLandingPageDisabled reflect the real settings instead of
  // silently defaulting to false.
  useEffect(() => {
    if (organization) {
      fetchSchema(organization.portalV2SchemaURL);
    }
  }, [organization, fetchSchema]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleEmailSubmit = useCallback(async () => {
    if (!emailOrPhone.trim()) return;

    const result = await beginLogin(emailOrPhone.trim());
    if (result) {
      setLoginResult(result);
      if (result.result === "sent-email" || result.result === "sent-sms") {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    }
  }, [emailOrPhone, beginLogin]);

  // Handle DOB value change - clear error when user types
  const handleDobChange = useCallback((value: string) => {
    setDateOfBirth(value);
    setDobError("");
  }, []);

  const handleDobSubmit = useCallback(async () => {
    if (!dateOfBirth) {
      setDobError(t("Date of birth is required"));
      return;
    }
    if (!/^\d{2}-\d{2}-\d{4}$/.test(dateOfBirth)) {
      setDobError(t("Please enter a valid date in MM-DD-YYYY format"));
      return;
    }

    const result = await beginLogin(emailOrPhone.trim(), dateOfBirth);
    if (result) {
      setLoginResult(result);
      if (result.result === "sent-email" || result.result === "sent-sms") {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else if (result.result === "requires-dob") {
        // Still requires DOB - probably wrong DOB, error will be shown via loginFlowError
        setDobError(t("Date of birth verification failed"));
      }
    }
  }, [dateOfBirth, emailOrPhone, beginLogin, t]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!password) return;

    const success = await authenticatePassword(emailOrPhone, password);
    if (success) {
      // Check if MFA was triggered (otpMode === "mfa") by accessing store directly
      // (React hooks won't have updated yet due to batching)
      const { otpMode } = useEnduserStore.getState();
      if (otpMode !== "mfa") {
        track(EngagementEventTypes.LOGIN, 3);
      }
      navigate("/", { replace: true });
    }
    // Error is displayed via loginFlowError state
  }, [emailOrPhone, password, authenticatePassword, navigate, track]);

  const handleOtpSubmit = useCallback(async () => {
    if (otpCode.length !== 6 || !loginFlowOtpToken) return;

    const success = await verifyLoginOtp(otpCode);
    if (success) {
      track(EngagementEventTypes.LOGIN, 3);
      navigate("/", { replace: true });
    }
  }, [otpCode, loginFlowOtpToken, verifyLoginOtp, navigate, track]);

  const handleResendOtp = useCallback(async () => {
    if (resendCooldown > 0) return;

    const success = await resendLoginOtp();
    if (success) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setOtpCode("");
    }
  }, [resendCooldown, resendLoginOtp]);

  const handleBack = useCallback(() => {
    if (step === "password-entry" || step === "otp-entry") {
      setPassword("");
      setOtpCode("");
      setLoginResult(null);
      clearLoginFlow();
    } else if (step === "dob-entry") {
      setDateOfBirth("");
      setDobError("");
      setLoginResult(null);
      clearLoginFlow();
    } else {
      navigate("/");
    }
  }, [step, clearLoginFlow, navigate]);

  const error = loginFlowError;

  // Format method name for display
  const formatMethodName = (result: LoginFlowResult["result"]): string => {
    if (result === "sent-email") return t("Email").toLowerCase();
    if (result === "sent-sms") return t("SMS").toLowerCase();
    return "";
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Wait for the schema fetch to resolve before rendering — otherwise isRegistrationDisabled
  // reads its default (false) and briefly shows "Sign up" even when it's disabled.
  if (organization && !hasAttemptedSchemaFetch) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t("Sign In")}</CardTitle>
          <CardDescription>
            {step === "email-entry" && (loginDescription || t("Enter your email or phone number"))}
            {step === "password-entry" && t("Enter your password")}
            {step === "otp-entry" &&
              loginResult &&
              t("Enter the 6-digit code sent to your {{method}}", {
                method: formatMethodName(loginResult.result),
              })}
            {step === "dob-entry" && t("For your security, please confirm your date of birth to continue.")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Step 1: Email/Phone Entry */}
          {step === "email-entry" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="emailOrPhone">{loginInputLabel || t("Email or Phone")}</Label>
                <Input
                  id="emailOrPhone"
                  type="text"
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  placeholder="email@example.com"
                  autoComplete="username"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && emailOrPhone.trim()) {
                      handleEmailSubmit();
                    }
                  }}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </>
          )}

          {/* Step 2a: Password Entry */}
          {step === "password-entry" && (
            <>
              <div className="space-y-2">
                <Label>{t("Email or Phone")}</Label>
                <Input value={emailOrPhone} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t("Password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    autoFocus
                    className="pr-10"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && password) {
                        handlePasswordSubmit();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/reset-password" className="text-sm text-primary hover:underline">
                  {t("Forgot Password?")}
                </Link>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </>
          )}

          {/* Step 2b: OTP Entry */}
          {step === "otp-entry" && (
            <>
              <div className="flex flex-col items-center space-y-4">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => setOtpCode(value.toUpperCase())}
                  autoFocus
                  inputMode="text"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-center">
                <Button
                  variant="link"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isLoading}
                  className="text-sm"
                >
                  {resendCooldown > 0 ? t("Resend in {{seconds}}s", { seconds: resendCooldown }) : t("Resend Code")}
                </Button>
              </div>
            </>
          )}

          {/* Step 2c: DOB Entry */}
          {step === "dob-entry" && (
            <>
              <div className="space-y-2">
                <Label>{t("Email or Phone")}</Label>
                <Input value={emailOrPhone} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">{t("Date of Birth")}</Label>
                <DobTextInput
                  id="dateOfBirth"
                  value={dateOfBirth}
                  onChange={handleDobChange}
                  placeholder="MM-DD-YYYY"
                  autoComplete="bday"
                  autoFocus
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter" && dateOfBirth) {
                      handleDobSubmit();
                    }
                  }}
                />
              </div>

              {(dobError || error) && (
                <Alert variant="destructive">
                  <AlertDescription>{dobError || error}</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-3">
          {/* Step 1: Continue button */}
          {step === "email-entry" && (
            <>
              <Button onClick={handleEmailSubmit} disabled={!emailOrPhone.trim() || isLoading} className="w-full">
                {isLoading ? t("Signing in...") : t("Continue")}
              </Button>
              {!isLandingPageDisabled && (
                <Button variant="ghost" onClick={handleBack} className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t("Back")}
                </Button>
              )}
              {!isRegistrationDisabled && (
                <div className="text-center text-sm">
                  <span className="text-muted-foreground">{t("Don't have an account?")} </span>
                  <Link to="/register" className="text-primary hover:underline">
                    {t("Sign up")}
                  </Link>
                </div>
              )}
            </>
          )}

          {/* Step 2a: Sign In button */}
          {step === "password-entry" && (
            <>
              <Button onClick={handlePasswordSubmit} disabled={!password || isLoading} className="w-full">
                {isLoading ? t("Signing in...") : t("Sign In")}
              </Button>
              <Button variant="ghost" onClick={handleBack} className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("Back")}
              </Button>
            </>
          )}

          {/* Step 2b: Verify button */}
          {step === "otp-entry" && (
            <>
              <Button onClick={handleOtpSubmit} disabled={otpCode.length !== 6 || isLoading} className="w-full">
                {isLoading ? t("Verifying...") : t("Verify")}
              </Button>
              <Button variant="ghost" onClick={handleBack} className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("Back")}
              </Button>
            </>
          )}

          {/* Step 2c: DOB Continue button */}
          {step === "dob-entry" && (
            <>
              <Button onClick={handleDobSubmit} disabled={!dateOfBirth || isLoading} className="w-full">
                {isLoading ? t("Verifying...") : t("Continue")}
              </Button>
              <Button variant="ghost" onClick={handleBack} className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("Back")}
              </Button>
            </>
          )}

          <div className="w-full flex justify-center pt-2">
            <LanguageSelector />
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};
